import { useState, useEffect } from 'react';
import { AuthProvider, useAuth, useFlights, useFriends, useFlightSearch } from '@/hooks';
import { Globe } from '@/components/globe';
import { AddFlightForm, EditFlightForm, FlightList, StatsDisplay, SearchBar } from '@/components/flights';
import { FriendsPanel, ComparisonDashboard } from '@/components/friends';
import { Header, GlobeControls, Instructions, Login, VerifyEmail, ProfileSettings, Help, WelcomeModal } from '@/components/ui';
import { getFriendFlights, deleteAccount } from '@/services';
import type { CreateFlightInput, UpdateFlightInput, FriendWithProfile, Flight, User, FriendStats } from '@/types';

// Authenticated app content - only rendered when user is logged in
function AuthenticatedContent({ user, signOut }: { user: User; signOut: () => void }) {
  const { flights, stats, isLoading: flightsLoading, addFlight, removeFlight, updateFlightDetails } = useFlights(
    user?.id || ''
  );
  const { searchQuery, setSearchQuery, filteredFlights } = useFlightSearch(flights);
  const {
    profile,
    friends,
    pendingRequests,
    sentRequests,
    updateProfile,
    searchUsers,
    sendFriendRequest,
    acceptFriendRequest,
    removeFriend,
    getFriendStats,
  } = useFriends();
  
  const [autoRotate, setAutoRotate] = useState(true);
  const [showFriendsPanel, setShowFriendsPanel] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [comparisonFriend, setComparisonFriend] = useState<{ friend: FriendWithProfile; stats: FriendStats } | null>(null);
  const [viewingFriend, setViewingFriend] = useState<{ friend: FriendWithProfile; flights: Flight[] } | null>(null);
  const [showMobilePanels, setShowMobilePanels] = useState(false);
  const [editingFlight, setEditingFlight] = useState<Flight | null>(null);

  // Check if this is the user's first login
  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem('hasSeenWelcome');
    if (!hasSeenWelcome) {
      setShowWelcome(true);
    }
  }, []);

  const handleCloseWelcome = () => {
    setShowWelcome(false);
    localStorage.setItem('hasSeenWelcome', 'true');
  };

  const handleAddFlight = async (input: CreateFlightInput) => {
    await addFlight(input);
  };

  const handleUpdateFlight = async (flightId: string, input: UpdateFlightInput) => {
    await updateFlightDetails(flightId, input);
  };

  const handleViewComparison = async (friend: FriendWithProfile) => {
    try {
      const friendStats = await getFriendStats(friend.friendId);
      setComparisonFriend({ friend, stats: friendStats });
      setShowFriendsPanel(false);
    } catch (error) {
      console.error('Failed to load friend stats:', error);
    }
  };

  const handleViewFriendGlobe = async (friend: FriendWithProfile) => {
    try {
      const friendFlights = await getFriendFlights(friend.friendId);
      setViewingFriend({ friend, flights: friendFlights });
      setShowFriendsPanel(false);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load friend\'s flights';
      alert(errorMessage);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await deleteAccount();
      // Sign out the user after successful account deletion
      signOut();
    } catch (error) {
      console.error('Failed to delete account:', error);
      throw error; // Re-throw to let ProfileSettings handle the error display
    }
  };

  const handleBackToMyGlobe = () => {
    setViewingFriend(null);
  };

  return (
    <div className="relative w-full h-full overflow-hidden">
      {/* Header */}
      <Header 
        userName={profile?.displayName || user?.name || user?.email}
        userProfilePicture={profile?.profilePicture}
        onSignOut={signOut}
        onOpenProfile={() => setShowProfileSettings(true)}
        onOpenFriends={() => setShowFriendsPanel(true)}
        onOpenHelp={() => setShowHelp(true)}
        pendingRequestsCount={pendingRequests.length}
        viewingFriendName={viewingFriend?.friend.profile?.displayName}
        onBackToMyGlobe={viewingFriend ? handleBackToMyGlobe : undefined}
      />

      {/* Globe (background) */}
      <div className="absolute inset-0 z-0">
        <Globe flights={viewingFriend ? viewingFriend.flights : filteredFlights} autoRotate={autoRotate} />
      </div>

      {/* Stats Panel */}
      <div className="absolute top-14 md:top-20 left-2 right-2 md:left-auto md:right-6 z-10 w-auto">
        <StatsDisplay 
          stats={stats} 
          showMobilePanels={showMobilePanels}
          onToggleMobilePanels={() => setShowMobilePanels(!showMobilePanels)}
          viewingFriend={!!viewingFriend}
        />
      </div>

      {/* Mobile Panel Close Button - when panels are open */}
      {!viewingFriend && showMobilePanels && (
        <button
          onClick={() => setShowMobilePanels(false)}
          className="md:hidden fixed top-16 md:top-20 z-30 glass rounded-full p-3 shadow-lg hover:bg-white/20 hover:scale-105 transition-all"
          style={{ right: '1.5rem' }}
          title="Hide panels"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* Left Sidebar - Add Flight & Flight List */}
      {!viewingFriend && (
        <div className={`absolute top-14 md:top-20 left-2 md:left-6 z-10 w-[calc(100vw-1rem)] sm:w-80 space-y-4 max-h-[calc(100vh-7rem)] md:max-h-[calc(100vh-120px)] overflow-y-auto flight-scroll mobile-scroll pr-2 transition-transform duration-300 ${
          showMobilePanels ? 'translate-x-0' : '-translate-x-[calc(100%+1rem)] md:translate-x-0'
        }`}>
          {editingFlight ? (
            <EditFlightForm
              flight={editingFlight}
              onUpdateFlight={handleUpdateFlight}
              onCancel={() => setEditingFlight(null)}
            />
          ) : (
            <AddFlightForm onAddFlight={handleAddFlight} />
          )}
          <SearchBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            resultsCount={filteredFlights.length}
          />
          <FlightList
            flights={filteredFlights}
            onDeleteFlight={removeFlight}
            onEditFlight={(flight) => setEditingFlight(flight)}
            isLoading={flightsLoading}
          />
        </div>
      )}

      {/* Mobile Panel Close Button - Friend View */}
      {viewingFriend && showMobilePanels && (
        <button
          onClick={() => setShowMobilePanels(false)}
          className="md:hidden fixed top-16 md:top-20 z-30 glass rounded-full p-3 shadow-lg hover:bg-white/20 hover:scale-105 transition-all"
          style={{ right: '1.5rem' }}
          title="Hide panels"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* Friend's Flight List */}
      {viewingFriend && (
        <div className={`absolute top-14 md:top-20 left-2 md:left-6 z-10 w-[calc(100vw-1rem)] sm:w-80 transition-transform duration-300 ${
          showMobilePanels ? 'translate-x-0' : '-translate-x-[calc(100%+1rem)] md:translate-x-0'
        }`}>
          <div className="glass rounded-2xl p-4 md:p-6">
            <h3 className="text-base md:text-lg font-semibold mb-4 flex items-center gap-2">
              <span className="text-primary-500">{viewingFriend.friend.profile?.displayName || 'Friend'}'s Flights</span>
            </h3>
            <div className="flight-scroll mobile-scroll space-y-3 max-h-[calc(100vh-12rem)] md:max-h-[calc(100vh-200px)] overflow-y-auto pr-2">
              {viewingFriend.flights.map((flight) => (
                <div key={flight.id} className="bg-white/5 rounded-xl p-3 md:p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 md:gap-3">
                      <span className="text-lg md:text-xl font-bold text-primary-500">
                        {flight.fromAirport.iata}
                      </span>
                      <span className="text-white/50">→</span>
                      <span className="text-lg md:text-xl font-bold text-primary-500">
                        {flight.toAirport.iata}
                      </span>
                    </div>
                  </div>
                  <div className="text-xs md:text-sm text-white/60 mt-2">
                    {flight.fromAirport.city} → {flight.toAirport.city}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Globe Controls */}
      <GlobeControls
        autoRotate={autoRotate}
        onToggleRotate={() => setAutoRotate(!autoRotate)}
      />

      {/* Instructions */}
      <Instructions />

      {/* Friends Panel */}
      {showFriendsPanel && (
        <FriendsPanel
          friends={friends}
          pendingRequests={pendingRequests}
          sentRequests={sentRequests}
          profile={profile}
          onClose={() => setShowFriendsPanel(false)}
          onSearchUsers={searchUsers}
          onSendRequest={sendFriendRequest}
          onAcceptRequest={acceptFriendRequest}
          onRemoveFriend={removeFriend}
          onViewComparison={handleViewComparison}
          onViewFriendGlobe={handleViewFriendGlobe}
        />
      )}

      {/* Profile Settings */}
      {showProfileSettings && (
        <ProfileSettings
          profile={profile}
          onClose={() => setShowProfileSettings(false)}
          onUpdateProfile={updateProfile}
          onDeleteAccount={handleDeleteAccount}
        />
      )}

      {/* Comparison Dashboard */}
      {comparisonFriend && (
        <ComparisonDashboard
          userStats={stats}
          friendStats={comparisonFriend.stats}
          friendName={comparisonFriend.friend.profile.displayName}
          onClose={() => setComparisonFriend(null)}
        />
      )}

      {/* Help Modal */}
      {showHelp && (
        <Help onClose={() => setShowHelp(false)} />
      )}

      {/* Welcome Modal */}
      {showWelcome && (
        <WelcomeModal
          onClose={handleCloseWelcome}
          onOpenHelp={() => {
            handleCloseWelcome();
            setShowHelp(true);
          }}
        />
      )}
    </div>
  );
}

// Main App wrapper with providers
export default function App() {
  return (
    <AuthProvider>
      <FlightGlobeApp />
    </AuthProvider>
  );
}
function FlightGlobeApp() {
  const { user, signOut, signIn, signInWithGoogle, signUp, confirmSignUp, confirmSignUpAndSignIn, resendConfirmationCode, handleOAuthCallback, isLoading } = useAuth();
  
  // Store email and password temporarily for auto-login after email verification
  // Note: Password is only held in memory during the signup flow and is not persisted
  // This is necessary to enable auto-login after verification, as AWS Cognito requires credentials
  const [pendingVerification, setPendingVerification] = useState<{ email: string; password: string } | null>(null);

  // Handle OAuth callback on component mount
  useEffect(() => {
    // Check for tokens in URL hash (implicit flow)
    const hash = window.location.hash;
    if (hash && (hash.includes('id_token') || hash.includes('access_token'))) {
      handleOAuthCallback().catch((error) => {
        console.error('OAuth callback failed:', error);
      });
    }
  }, [handleOAuthCallback]);

  // Show login screen if not authenticated
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  // Show verification screen if user just signed up
  if (pendingVerification) {
    return (
      <VerifyEmail
        email={pendingVerification.email}
        password={pendingVerification.password}
        onConfirm={async (email, code, password) => {
          // Auto-login after verification if password is available
          if (password) {
            await confirmSignUpAndSignIn(email, code, password);
          } else {
            // This shouldn't happen, but if password is missing, just verify without auto-login
            await confirmSignUp(email, code);
          }
          setPendingVerification(null);
        }}
        onResendCode={resendConfirmationCode}
        onBack={() => setPendingVerification(null)}
      />
    );
  }

  if (!user) {
    return (
      <Login
        onSignIn={signIn}
        onSignInWithGoogle={signInWithGoogle}
        onSignUp={signUp}
        onVerificationNeeded={(email, password) => setPendingVerification({ email, password })}
      />
    );
  }

  // User is authenticated, render the main app
  return <AuthenticatedContent user={user} signOut={signOut} />;
}

// Test CI pipeline
